<template>
  <div class="AIAvatarSlideSelectionContainer">
    <!-- this is the "normal" filter panel -->
    <b-dropdown ref="dropDownFilter" position="is-bottom-left"
                aria-role="menu" class="dropdownAIAvatarSelectionSlideTemplate"
                expanded
                animation="fadeLeft" :max-height="450" @active-change="onOpenChange"
                :can-close="true">
      <w-button class="buttonAIAvatarSelection"
                icon="view-dashboard-outline"
                size="small" :tooltip="$t('aiAvatar.tooltip.avatarSlideTemplateSelection')"
                color="text" slot="trigger" />

      <b-dropdown-item class="noHover" :focusable="false"
                       custom
                       paddingless>
        <div class="slideTemplateSelectionPanel">
          <div class="slideSelectionTitle">
            {{ $t("aiAvatar.selectSlideTemplate") }}
          </div>
          <div class="colorpickerElement" :style="'background:'+themeColor"
               @click="showColorPicker=!showColorPicker"></div>
          <Transition name="fadeLeft">
            <div class="colorPickerPosition" v-show="showColorPicker">
              <w-color-picker v-model="themeColor" @input="onColorChange"
                              :inline="true" :title="$t('aiAvatar.chooseTheme')"
                              position="bottom-left" size="large" representation="square"
                              :empty-formater="true" custom-class="colorPickerHack" />
              <div class="closeColorPickerButton" @click="showColorPicker=false">
                <w-icon icon="close" />
              </div>
            </div>
          </Transition>
          <perfect-scrollbar ref="thumbnailScroll">

            <div class="thumbnailGrid">
              <!-- AVATAR LEFT -->
              <div class="templateElement" :class="{selected:isAvatarLeftSelected}"
                   @click="selectTemplate('AVATAR_LEFT')"
                   role="img"
                   aria-label="Template Avatar left">
                <div class="avatarLayout leftLayout" :style="'background-image:url('+backgroundSelected?.url+')'">
                  <div class="avatarProfile" :style="'background-image:url('+avatarItemSelected.img+')'"></div>
                </div>
                <div class="slideLayout rightLayout">
                  <div class="titleElement" :style="'color:'+themeColor">
                    {{ $t("aiAvatar.slideTemplate.titlePlaceholder") }}
                  </div>
                  <div class="descriptionElement">{{ $t("aiAvatar.slideTemplate.descriptionPlaceholder") }}</div>
                </div>
                <div class="templateSelectedCheck" v-if="isAvatarLeftSelected">
                  <w-icon icon="check-circle" color="primary" class="checkIcon" />
                </div>
              </div>

              <!-- AVATAR RIGHT -->
              <div class="templateElement" :class="{selected:isAvatarRightSelected}"
                   @click="selectTemplate('AVATAR_RIGHT')"
                   role="img"
                   aria-label="Template Avatar right">

                <div class="slideLayout leftLayout">
                  <div class="titleElement" :style="'color:'+themeColor">
                    {{ $t("aiAvatar.slideTemplate.titlePlaceholder") }}
                  </div>
                  <div class="descriptionElement">{{ $t("aiAvatar.slideTemplate.descriptionPlaceholder") }}</div>
                </div>
                <div class="avatarLayout rightLayout" :style="'background-image:url('+backgroundSelected?.url+')'">
                  <div class="avatarProfile" :style="'background-image:url('+avatarItemSelected.img+')'"></div>
                </div>
                <div class="templateSelectedCheck" v-if="isAvatarRightSelected">
                  <w-icon icon="check-circle" color="primary" />
                </div>
              </div>

              <!-- TITLE -->
              <div class="templateElement" :class="{selected:isTitleSelected}" @click="selectTemplate('TITLE')"
                   role="img"
                   aria-label="Template Title">
                <div class="slideLayout">
                  <div class="titleElement" :style="'color:'+themeColor">
                    {{ $t("aiAvatar.slideTemplate.titlePlaceholder") }}
                  </div>
                  <div class="descriptionElement">{{ $t("aiAvatar.slideTemplate.descriptionPlaceholder") }}</div>
                </div>
                <div class="templateSelectedCheck" v-if="isTitleSelected">
                  <w-icon icon="check-circle" color="primary" />
                </div>
              </div>

              <!-- TITLE_ILLUSTRATED -->
              <div class="templateElement" :class="{selected:isTitleIllustratedSelected}"
                   @click="selectTemplate('TITLE_ILLUSTRATED')"
                   role="img"
                   aria-label="Template illustrated title">
                <VideoImageBackground :mask="'light'" :animate="false" :url="slideImage" />

                <div class="slideLayout">
                  <div class="titleElement" :style="'color:'+themeColor">
                    {{ $t("aiAvatar.slideTemplate.titlePlaceholder") }}
                  </div>
                  <div class="descriptionElement">{{ $t("aiAvatar.slideTemplate.descriptionPlaceholder") }}</div>
                </div>
                <div class="templateSelectedCheck" v-if="isTitleIllustratedSelected">
                  <w-icon icon="check-circle" color="primary" />
                </div>
              </div>

              <!-- ILLUSTRATED LEFT -->
              <div class="templateElement" :class="{selected:isIllustratedLeft}"
                   @click="selectTemplate('ILLUSTRATED_LEFT')"
                   role="img"
                   aria-label="Template illustrated left">
                <div class="imageLayout left">
                  <VideoImageBackground :url="slideImage" />
                </div>
                <div class="slideLayout rightLayout">
                  <div class="titleElement" :style="'color:'+themeColor">
                    {{ $t("aiAvatar.slideTemplate.titlePlaceholder") }}
                  </div>
                  <div class="descriptionElement">{{ $t("aiAvatar.slideTemplate.descriptionPlaceholder") }}</div>
                </div>
                <div class="templateSelectedCheck" v-if="isIllustratedLeft">
                  <w-icon icon="check-circle" color="primary" class="checkIcon" />
                </div>
              </div>

              <!-- ILLUSTRATED RIGHT -->
              <div class="templateElement" :class="{selected:isIllustratedRight}"
                   @click="selectTemplate('ILLUSTRATED_RIGHT')"
                   role="img"
                   aria-label="Template illustrated right">

                <div class="slideLayout leftLayout">
                  <div class="titleElement" :style="'color:'+themeColor">
                    {{ $t("aiAvatar.slideTemplate.titlePlaceholder") }}
                  </div>
                  <div class="descriptionElement">{{ $t("aiAvatar.slideTemplate.descriptionPlaceholder") }}</div>
                </div>
                <div class="imageLayout rightLayout">
                  <VideoImageBackground :url="slideImage" />
                </div>
                <div class="templateSelectedCheck" v-if="isIllustratedRight">
                  <w-icon icon="check-circle" color="primary" />
                </div>
              </div>

              <!-- ILLUSTRATED ONLY -->
              <div class="templateElement" :class="{selected:isIllustratedOnly}"
                   @click="selectTemplate('ILLUSTRATED_ONLY')"
                   role="img"
                   aria-label="Template illustrated only">

                <div class="imageLayout onlyLayout">
                  <VideoImageBackground :url="slideImage" />
                </div>
                <div class="templateSelectedCheck" v-if="isIllustratedRight">
                  <w-icon icon="check-circle" color="primary" />
                </div>
              </div>

              <!-- AVATAR ONLY -->
              <div class="templateElement" :class="{selected:isAvatarOnly}" @click="onSlideLayoutUpdated('AVATAR_ONLY')"
                   role="img"
                   aria-label="Avatar only">
                <div class="avatarLayout" :style="'background-image:url('+backgroundSelected?.url+')'">
                  <div class="avatarProfile" :style="'background-image:url('+avatarItemSelected.img+')'"></div>
                </div>
                <div class="templateSelectedCheck" v-if="isAvatarOnly">
                  <w-icon icon="check-circle" color="primary" />
                </div>
              </div>

              <!-- AVATAR + DOC -->
              <div class="templateElement" :class="{selected:isAvatarBottomLeft}" @click="onSlideLayoutUpdated('AVATAR_BOTTOM_LEFT')"
              v-if="documentImage" :style="'background-image: url('+documentImage+');'"
                   role="img"
                   aria-label="Avatar + document">
                <div class="avatarLayout sticker" :style="'background-image:url('+backgroundSelected?.url+')'">
                  <div class="avatarProfile" :style="'background-image:url('+avatarItemSelected.img+')'"></div>
                </div>
                <div class="templateSelectedCheck" v-if="isAvatarBottomLeft">
                  <w-icon icon="check-circle" color="primary" />
                </div>
              </div>
              <!-- VOICE + DOC -->
              <div class="templateElement" :class="{selected:isVoiceOnly}" @click="onSlideLayoutUpdated('VOICE_ONLY')"
              v-if="documentImage" :style="'background-image: url('+documentImage+');'"
                   role="img"
                   aria-label="Template Voice + document">
                <div class="templateSelectedCheck" v-if="isVoiceOnly">
                  <w-icon icon="check-circle" color="primary" />
                </div>
              </div>

              <!-- AVATAR + VIDEO -->
              <div class="templateElement" :class="{selected:isAvatarBottomLeft}" @click="onSlideLayoutUpdated('AVATAR_BOTTOM_LEFT')"
                   v-if="isVideoAvailable"
                   role="img"
                   aria-label="Avatar + document">
                <video class="videoElementBackgroundAvatar"  muted autoplay loop :src="videoUrl||'https://storage.googleapis.com/weet_prod_data/welcome-weet.mp4'" />

                <div class="avatarLayout sticker" :style="'background-image:url('+backgroundSelected?.url+')'">
                  <div class="avatarProfile" :style="'background-image:url('+avatarItemSelected.img+')'"></div>
                </div>
                <div class="templateSelectedCheck" v-if="isAvatarBottomLeft">
                  <w-icon icon="check-circle" color="primary" />
                </div>
              </div>
              <!-- VOICE + VIDEO -->
              <div class="templateElement" :class="{selected:isVoiceOnly}" @click="onSlideLayoutUpdated('VOICE_ONLY')"
                   v-if="isVideoAvailable"
                   role="img"
                   aria-label="Template Voice + video">
                <video class="videoElementBackgroundAvatar"  muted autoplay loop :src="videoUrl||'https://storage.googleapis.com/weet_prod_data/welcome-weet.mp4'" />

                <div class="templateSelectedCheck" v-if="isVoiceOnly">
                  <w-icon icon="check-circle" color="primary" />
                </div>
              </div>



              <!-- Upload a document if need -->
              <div class="templateElement"  v-if="!documentImage && !isVideoAvailable" @click="onUploadDocument('VOICE_ONLY')"
                   role="img"
                   aria-label="Upload a document">
                <div class="uploadContainer">
                <div class="iconUpload">
                  <w-icon size="default" icon="file-upload-outline"/>
                </div>
                <div class="labelUpload">
                  {{$t('aiAvatar.templateSelectionUpload')}}
                </div>
                </div>
              </div>
            </div>
          </perfect-scrollbar>
        </div>
      </b-dropdown-item>
    </b-dropdown>
  </div>
</template>

<script lang="ts">
import Vue from "vue";
import { Component } from "vue-property-decorator";
import store from "@/store";
import WButton from "@/components/wrapper/w-button.vue";
import AIAvatarSelectionPanel from "@/components/recorder/aiAvatar/panel/AIAvatarSelectionPanel.vue";
import { LIST_OF_AVATAR, LIST_OF_BACKGROUND } from "@/store/AIAvatar/aiavatarGenerationModel";
import WIcon from "@/components/wrapper/w-icon.vue";
import WColorPicker from "@/components/wrapper/w-colorPicker.vue";
import { ScriptSlideLayout, Slide, SlideTemplate } from "@/store/slides2Video/slides2VideoModels";
import { txtColorForBackground } from "@/utils/color";
import { clone } from "@/utils/cloneUtil";
import { updateCurrentSlide } from "@/utils/slides/slideUtil";
import { SAVE_SLIDE_THEME } from "@/store/persistedInformation/persistAction";
import VideoImageBackground from "@/components/videoImageBackground/VideoImageBackground.vue";
import { OPEN_AVATAR_EDITING_CONTENT, SELECT_AI_SCRIPT_SLIDE_LAYOUT } from "@/store/AIAvatar/AIAVatarAction";
import { SELECT_SEGMENT } from "@/store/recordingState/recordStateAction";


@Component({
  components: { VideoImageBackground, WColorPicker, WIcon, AIAvatarSelectionPanel, WButton },
  computed: {}
})
export default class AIAvatarSlideTemplateSelectionButton extends Vue {

  // State
  themeColor = "#7957D5";
  showColorPicker: boolean = false;


  get avatarItemSelected() {
    return LIST_OF_AVATAR.filter((avatar) => {
      if (avatar.avatar === this.avatarSelected) {
        return true;
      } else {
        return false;
      }
    })[0];
  }

  get avatarSelected() {
    return store.getters.getAvatarSelected;
  }

  get backgroundSelected() {
    return LIST_OF_BACKGROUND.find((b) => {
      return store.getters.getAvatarBabckgroundSelected === b.background;
    });
  }

  get currentSlide(): Slide | null {
    if (store.getters.getCurrentSlideIndex > -1) {
      return store.getters.slides[store.getters.getCurrentSlideIndex];
    }
    return null;
  }

  get isSlideLayout() {
    return this.currentSlide?.layout === ScriptSlideLayout.SLIDE || store.getters.getAvatarScriptSlideLayout === ScriptSlideLayout.SLIDE;
  }

  get templateSelected() {
    return this.currentSlide?.slideTemplate || SlideTemplate.AVATAR_LEFT;
  }

  get slideImage() {
    return this.currentSlide?.slideImageB64 || "";
  }
  get documentImage(){
    if(this.currentSlide?.originalDocumentB64){
      return  "data:image/jpg;base64," + this.currentSlide?.originalDocumentB64
    }
    return '';
  }

  get isVideoAvailable(){
    return !!this.currentSlide?.fileID && this.currentSlide?.videoTimestampEnd>0;
  }
  get videoUrl(){
    return this.currentSlide?.chunkVideoUrl ||''
  }

  get txtColor() {
    return txtColorForBackground(this.themeColor);
  }

  get isTitleSelected(): boolean {
    return this.isSlideLayout && SlideTemplate.TITLE === this.templateSelected;
  }

  get isAvatarLeftSelected(): boolean {
    return this.isSlideLayout && SlideTemplate.AVATAR_LEFT === this.templateSelected;
  }

  get isAvatarRightSelected(): boolean {
    return this.isSlideLayout && SlideTemplate.AVATAR_RIGHT === this.templateSelected;
  }

  get isTitleIllustratedSelected(): boolean {
    return this.isSlideLayout && SlideTemplate.TITLE_ILLUSTRATED === this.templateSelected;
  }

  get isIllustratedLeft(): boolean {
    return this.isSlideLayout && SlideTemplate.ILLUSTRATED_LEFT === this.templateSelected;
  }

  get isIllustratedRight(): boolean {
    return this.isSlideLayout && SlideTemplate.ILLUSTRATED_RIGHT === this.templateSelected;
  }
  get isIllustratedOnly():boolean{
    return  this.isSlideLayout && SlideTemplate.ILLUSTRATED_ONLY === this.templateSelected;
  }
  get isAvatarOnly(){
    return !this.isSlideLayout &&
      this.currentSlide?.layout===ScriptSlideLayout.AVATAR_ONLY;
  }
  get isAvatarBottomLeft(){

    return !this.isSlideLayout &&
      this.currentSlide?.layout===ScriptSlideLayout.AVATAR_BOTTOM_LEFT
  }
  get isVoiceOnly(){
    return !this.isSlideLayout &&
      this.currentSlide?.layout===ScriptSlideLayout.VOICE_ONLY
  }



  mounted() {
    this.themeColor = store.getters.getFavSlideTheme || store.getters.getThemeColor;
  }

  public onOpenChange(value) {
    this.showColorPicker = false;
    this.themeColor = this.currentSlide?.slideTheme ? this.currentSlide.slideTheme : this.themeColor;
    this.$emit("open", value);
  }


  public onColorChange(color) {
    const newSlide = clone(this.currentSlide);
    newSlide.slideTheme = color;
    store.dispatch(SAVE_SLIDE_THEME, color);
    updateCurrentSlide(newSlide);
  }

  private selectTemplate(template: SlideTemplate) {
    const newSlide = clone(this.currentSlide);
    newSlide.slideTemplate = template;
    updateCurrentSlide(newSlide);
    store.dispatch(OPEN_AVATAR_EDITING_CONTENT,true)
    this.onSlideLayoutUpdated(ScriptSlideLayout.SLIDE)

  }

  private onUploadDocument() {
    store.dispatch(SELECT_SEGMENT,null)
    store.dispatch(OPEN_AVATAR_EDITING_CONTENT,false)
    this.onSlideLayoutUpdated(ScriptSlideLayout.VOICE_ONLY)
  }
  private onSlideLayoutUpdated(value) {
    // update slides
    if (this.currentSlide) {
      const newSlide = clone(this.currentSlide) as Slide;
      newSlide.layout = value;
      updateCurrentSlide(newSlide);
    }
    store.dispatch(SELECT_AI_SCRIPT_SLIDE_LAYOUT, value);

    // @ts-ignore
    this.$refs.dropDownFilter?.toggle();
  }

}
</script>

<style lang="scss">
@import '@/scss/gridBreakPoint.scss';

.dropdownAIAvatarSelectionSlideTemplate {
  .dropdown-menu {
    margin-top: -372px;
    margin-right: 48px;
    margin-left: -48px;
    max-height: 500px;
    min-width: 256px;
    border-radius: 16px;
    @include GRID2X {
      margin-top: -0px;
      margin-right: 0px;
    }
    @include GRID1X {
      margin-top: -0px;
      margin-right: 0px;
    }

    .dropdown-content{
      width: 560px;
      //height: 536px;
      margin-left: 320px;
    }
  }
}


</style>


<style lang="scss" scoped>
.slideTemplateSelectionPanel {
  width: 100%;

  .slideSelectionTitle {
    font-size: 20px;
    color: var(--black);
    font-weight: 900;
    padding-left: 16px;
    margin-top: 8px;
    margin-bottom: 8px;
  }

  .ps {
    padding-left: 16px;
    padding-right: 16px;
    max-height: 470px;
  }

  .colorpickerElement {
    position: absolute;
    top: 4px;
    cursor: pointer;
    right: 16px;
    width: 24px;
    height: 24px;
    //overflow: hidden;
    box-shadow: 0px 0px 10px rgba(0, 0, 0, 0.5);
    border: solid 1px white;
    border-radius: 8px;

    &:hover {
      box-shadow: 0px 0px 10px rgba(0, 0, 0, 0.8);
    }
  }

  .colorPickerPosition {
    position: absolute;
    animation-duration: 0.5s;
    top: 220px;
    left: 302px;
    height: 0px;
    width: 0px;
    ::v-deep .dropdown-menu{
      margin-top:-220px;
    }
    ::v-deep .dropdown-content{
      margin-left: 312px;
      width:232px;
    }
    .labelColorPicker{
      font-weight: 900;
      font-size: 20px;
    }
    .closeColorPickerButton {
      position: absolute;
      top: -214px;
      right: -492px;
      width: 32px;
      height: 32px;
      display: flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;
    }
  }


  .thumbnailGrid {
    display: grid;
    grid-template-columns: 1fr 1fr 1fr;
    grid-column-gap: 16px;

    .templateElement {
      margin-top: 8px;
      margin-bottom: 16px;
      cursor: pointer;
      position: relative;
      aspect-ratio: 16/9;
      border-radius: 8px;
      width: 165px;
      background-color: var(--light);
      background-size: contain;
      background-position: center center;
      background-repeat: no-repeat;
      border: solid 2px var(--light);
      image-rendering: crisp-edges;
      display: flex;
      overflow: hidden;

      &:hover {
        border-color: var(--light1);
      }

      &.selected {
        border-color: var(--primary);
      }

      .avatarLayout {
        width: 100%;
        height: 100%;
        position: absolute;
        background-size: cover;
        background-position: center center;

        &.leftLayout {
          left: 0;
          width: 40%;
        }

        &.rightLayout {
          right: 0;
          width: 40%;
        }
        &.sticker{
          overflow: hidden;
          width: 48px;
          height:48px;
          border-radius: 100% 100% 100% 2px;
          bottom: 8px;
          left: 8px;
        }

        .avatarProfile {
          position: absolute;
          height: 100%;
          top: 0;
          left: 0;
          width: 100%;
          background-size: cover;
          background-position: center center;
        }
      }

      .imageLayout {
        width: 40%;
        height: 100%;
        position: absolute;

        &.leftLayout {
          left: 0;
        }

        &.rightLayout {
          right: 0;
        }
        &.onlyLayout{
          left: 0;
          width: 100%;
        }
      }

      .slideLayout {
        text-align: center;
        color: white;
        position: absolute;
        width: 100%;
        height: 100%;
        transition: all 0.5s ease-in-out;

        &.leftLayout {
          width: 60%;
          left: 0;
        }

        &.rightLayout {
          width: 60%;
          right: 0;
        }

        .titleElement {
          margin-top: 8px;
          font-size: 14px;
          font-weight: 700;
        }

        .descriptionElement {
          margin-top: 8px;
          font-size: 12px;
          color: var(--black);
        }
      }
    }

    .slideSelectionLabel {
      display: block;
      font-size: 14px;
      font-weight: 700;
      margin-top: 4px;
      margin-bottom: 8px;
    }

    .templateSelectedCheck {
      height: 24px;
      width: 24px;
      border-radius: 100%;
      top: 0px;
      background: white;
      right: 0px;
      position: absolute;
    }
  }
  .uploadContainer{
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    height: 100%;
    width: 100%;
    cursor: pointer;
  }
}
</style>
